"use client";

import { useCallback, useRef, useState, type FormEvent } from "react";
import { useQuery } from "convex/react";
import { MessageSquarePlus } from "@/components/icons";
import { api } from "@convex/_generated/api";

import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { redactUrlCredentials } from "@shared/security/redact-url";
import { useSubmissionKey } from "@/lib/support/use-submission-key";
import { useSupportRequestSubmission } from "@/lib/support/use-support-request-submission";

interface Draft {
  email: string;
  message: string;
  honeypot: string;
}

const EMPTY_DRAFT: Draft = { email: "", message: "", honeypot: "" };

type Submission =
  | { state: "editing"; error: string | null }
  | { state: "sending" }
  | { state: "sent"; ticketNumber?: string };

function useFeedbackSession() {
  const [submission, setSubmission] = useState<Submission>({
    state: "editing",
    error: null,
  });
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [session, setSession] = useState(0);
  const sessionRef = useRef(0);
  const submissionKey = useSubmissionKey();
  const keyedFingerprintRef = useRef<string | null>(null);

  const onSubmission = useCallback((next: Submission, from: number) => {
    if (from !== sessionRef.current) return;
    setSubmission(next);
  }, []);

  const keyForSubmission = useCallback(
    (fingerprint: string) => {
      const keyed = keyedFingerprintRef.current;
      if (keyed !== null && keyed !== fingerprint) submissionKey.reset();
      keyedFingerprintRef.current = fingerprint;
      return submissionKey.current();
    },
    [submissionKey],
  );

  function close() {
    sessionRef.current += 1;
    setSession(sessionRef.current);
    if (submission.state === "sent") {
      submissionKey.reset();
      keyedFingerprintRef.current = null;
      setDraft(EMPTY_DRAFT);
    }
    setSubmission({ state: "editing", error: null });
  }

  return {
    submission,
    draft,
    setDraft,
    session,
    keyForSubmission,
    onSubmission,
    close,
  };
}

export function SiteFeedbackDialog({
  variant = "icon",
}: {
  variant?: "icon" | "row";
}) {
  const [open, setOpen] = useState(false);
  const feedback = useFeedbackSession();

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) feedback.close();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FeedbackTrigger variant={variant} />
      <DialogContent className="sm:max-w-md">
        {feedback.submission.state === "sent" ? (
          <FeedbackSent
            ticketNumber={feedback.submission.ticketNumber}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          <FeedbackForm
            session={feedback.session}
            keyForSubmission={feedback.keyForSubmission}
            submission={feedback.submission}
            draft={feedback.draft}
            onDraftChange={feedback.setDraft}
            onSubmission={feedback.onSubmission}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function FeedbackTrigger({ variant }: { variant: "icon" | "row" }) {
  if (variant === "row") {
    return (
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="auto"
          className="min-h-11 w-full justify-start px-3 text-sm font-normal text-muted-foreground"
        >
          <MessageSquarePlus className="size-4" />
          Send feedback
        </Button>
      </DialogTrigger>
    );
  }
  return (
    <DialogTrigger asChild>
      <IconButton
        size="compact"
        className="w-8"
        aria-label="Leave feedback"
      >
        <MessageSquarePlus className="size-4" />
      </IconButton>
    </DialogTrigger>
  );
}

function FeedbackSent({
  ticketNumber,
  onClose,
}: {
  ticketNumber?: string;
  onClose: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Thanks — we have it</DialogTitle>
        <DialogDescription>
          {ticketNumber ? `Logged as ${ticketNumber}.` : "Logged."}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="primary" type="button" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </>
  );
}

function useFeedbackSubmit(
  draft: Draft,
  fallbackEmail: string,
  session: number,
  keyForSubmission: (fingerprint: string) => string,
  onSubmission: (next: Submission, session: number) => void,
) {
  const submitFeedback = useSupportRequestSubmission();

  return useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (draft.message.trim().length === 0) {
        onSubmission(
          { state: "editing", error: "Tell us what you would change." },
          session,
        );
        return;
      }
      onSubmission({ state: "sending" }, session);
      const contactEmail = fallbackEmail || draft.email;
      try {
        const result = await submitFeedback({
          contactEmail,
          issueType: "feedback",
          message: draft.message,
          honeypot: draft.honeypot,
          submittedFrom: "in_app_feedback",
          pagePath: redactUrlCredentials(window.location.href),
          submissionKey: keyForSubmission(`${contactEmail}\u0000${draft.message}`),
        });
        onSubmission(
          { state: "sent", ticketNumber: result.ticketNumber },
          session,
        );
      } catch {
        onSubmission(
          {
            state: "editing",
            error: "That did not send. Check the address and try again.",
          },
          session,
        );
      }
    },
    [draft, fallbackEmail, keyForSubmission, onSubmission, session, submitFeedback],
  );
}

interface FeedbackFormProps {
  session: number;
  keyForSubmission: (fingerprint: string) => string;
  submission: Submission;
  draft: Draft;
  onDraftChange: (next: Draft) => void;
  onSubmission: (next: Submission, session: number) => void;
}

function FeedbackForm({
  session,
  keyForSubmission,
  submission,
  draft,
  onDraftChange,
  onSubmission,
}: FeedbackFormProps) {
  const currentUser = useQuery(api.users.queries.current);
  const signedInEmail = currentUser?.user?.email ?? "";
  const onSubmit = useFeedbackSubmit(
    draft,
    signedInEmail,
    session,
    keyForSubmission,
    onSubmission,
  );

  return (
    <form onSubmit={onSubmit}>
      <DialogHeader>
        <DialogTitle>Leave feedback</DialogTitle>
      </DialogHeader>

      <FeedbackFields
        draft={draft}
        onChange={onDraftChange}
        askForEmail={!signedInEmail}
        error={submission.state === "editing" ? submission.error : null}
      />

      <DialogFooter>
        <Button
          variant="primary"
          type="submit"
          disabled={submission.state === "sending"}
        >
          {submission.state === "sending" ? "Sending…" : "Send feedback"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function FeedbackFields({
  draft,
  onChange,
  askForEmail,
  error,
}: {
  draft: Draft;
  onChange: (next: Draft) => void;
  askForEmail: boolean;
  error: string | null;
}) {
  return (
    <div className="grid gap-4 py-4">
      {askForEmail && (
        <EmailField
          value={draft.email}
          onChange={(email) => onChange({ ...draft, email })}
        />
      )}
      <div className="grid gap-1.5">
        <Label htmlFor="feedback-message">Feedback</Label>
        <Textarea
          id="feedback-message"
          required
          rows={5}
          placeholder="What would you change?"
          value={draft.message}
          onChange={(event) =>
            onChange({ ...draft, message: event.target.value })
          }
        />
      </div>
      <HoneypotField
        value={draft.honeypot}
        onChange={(honeypot) => onChange({ ...draft, honeypot })}
      />
      {error && (
        <p role="alert" className="text-sm text-destructive-strong">
          {error}
        </p>
      )}
    </div>
  );
}

function EmailField({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor="feedback-email">Email</Label>
      <Input
        id="feedback-email"
        type="email"
        required
        autoComplete="email"
        placeholder="you@company.com"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function HoneypotField({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <input
      type="text"
      name="company_website"
      tabIndex={-1}
      aria-hidden
      autoComplete="off"
      className="absolute -left-[9999px] top-0 h-px w-px overflow-hidden opacity-0"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
